import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Delete as DeleteIcon } from '@mui/icons-material';

import { StyledTextField } from 'components/Input/styles';
import { SLASH_FULL_DATE_FORMAT } from 'core/constants';
import { formatFloating } from 'utils/formatters/timezone';

import { getLegendColor, isRiskRatingValue } from '../utils';

import type { CellValue, Column, RiskRatingValue, Row } from '../types';
import {
  ACTION_COLUMN_WIDTH,
  DEFAULT_COLUMN_WIDTH,
  LEGEND_MIN_AXIS,
  MIN_COLUMN_WIDTH,
  MIN_RATING_COLUMN_WIDTH,
  MIN_TEXT_COLUMN_WIDTH,
  ROW_NUMBER_COLUMN_WIDTH,
} from '../constants';
import {
  StyledBodyCell,
  StyledCellInputWrapper,
  StyledColumnResizer,
  StyledEmptyState,
  StyledHeaderCell,
  StyledReadOnlyCellText,
  StyledLegendItem,
  StyledLegendStrip,
  StyledLegendSwatch,
  StyledCustomScrollbar,
  StyledCustomScrollbarThumb,
  StyledRiskAssessmentTable,
  StyledRiskAssessmentTableWrapper,
  StyledTableScrollContainer,
  StyledRowActionsCell,
  StyledRowDeleteButton,
  StyledRowNumberCell,
  StyledRowNumberHeaderCell,
} from '../styles';
import { DateCell } from './date-cell';
import { RiskRatingCell } from './risk-rating-cell';
import { RiskRatingMatrixPopover } from './risk-rating-matrix-popover';

interface RiskAssessmentTableProps {
  columns: Column[];
  rows: Row[];
  likelihoodLabels: string[];
  severityLabels: string[];
  editable: boolean;
  onChange: (rowId: string, columnId: string, value: CellValue) => void;
  onDelete: (rowId: string) => void;
  onColumnResize: (columnId: string, width: number) => void;
}

const getRiskRating = (v: CellValue): RiskRatingValue =>
  isRiskRatingValue(v) ? v : { likelihood: null, severity: null };

export const RiskAssessmentTable: React.FC<RiskAssessmentTableProps> = ({
  columns: columnsProp,
  rows: rowsProp,
  likelihoodLabels: likelihoodLabelsProp,
  severityLabels: severityLabelsProp,
  editable,
  onChange,
  onDelete,
  onColumnResize,
}) => { 
  const columns = Array.isArray(columnsProp) ? columnsProp : [];
  const rows = Array.isArray(rowsProp) ? rowsProp : [];
  const likelihoodLabels = Array.isArray(likelihoodLabelsProp) ? likelihoodLabelsProp : [];
  const severityLabels = Array.isArray(severityLabelsProp) ? severityLabelsProp : [];
  const tableRef = useRef<HTMLTableElement | null>(null);
  const colRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const headerRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);

  // Custom horizontal scrollbar state. We draw our own (native bars auto-hide as
  // overlays on macOS, so they're not a reliable "scroll sideways" cue). The thumb
  // size/position track the wrapper's scroll metrics; shown only when it overflows.
  const [scrollbar, setScrollbar] = useState<{ visible: boolean; widthPct: number; leftPct: number }>(
    { visible: false, widthPct: 0, leftPct: 0 }
  );

  const syncScrollbar = useCallback((): void => {
    const el = scrollWrapperRef.current;
    if (!el) return;
    const { scrollWidth, clientWidth, scrollLeft } = el;
    if (scrollWidth <= clientWidth + 1) {
      setScrollbar((s) => (s.visible ? { visible: false, widthPct: 0, leftPct: 0 } : s));
      return;
    }
    setScrollbar({
      visible: true,
      widthPct: (clientWidth / scrollWidth) * 100,
      leftPct: (scrollLeft / scrollWidth) * 100,
    });
  }, []);

  useEffect(() => {
    const el = scrollWrapperRef.current;
    if (!el) return undefined;
    syncScrollbar();
    el.addEventListener('scroll', syncScrollbar, { passive: true });
    // Catch width changes from column resizing and viewport/zoom changes too.
    // Guarded because jsdom (unit tests) has no ResizeObserver.
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => syncScrollbar()) : null;
    observer?.observe(el);
    if (observer && tableRef.current) observer.observe(tableRef.current);
    return () => {
      el.removeEventListener('scroll', syncScrollbar);
      observer?.disconnect();
    };
  }, [syncScrollbar, columns, rows, editable]);

  const handleScrollThumbMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    const el = scrollWrapperRef.current;
    const track = e.currentTarget.parentElement;
    if (!el || !track) return;
    const startX = e.clientX;
    const startScroll = el.scrollLeft;
    const trackWidth = track.clientWidth;
    const thumbWidth = (el.clientWidth / el.scrollWidth) * trackWidth;
    const scrollRange = el.scrollWidth - el.clientWidth;
    const travel = trackWidth - thumbWidth;
    // px the cursor moves -> px the content scrolls (thumb travel maps to scroll range).
    const ratio = travel > 0 ? scrollRange / travel : 0;

    const handleMove = (ev: MouseEvent): void => {
      el.scrollLeft = startScroll + (ev.clientX - startX) * ratio;
    };
    const handleUp = (): void => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  useEffect(
    () => (): void => {
      // Belt-and-braces: if the component unmounts mid-drag (e.g. an error
      // boundary trips), tear down the window listeners so a stuck mousemove
      // can't keep firing into a dead component.
      dragCleanupRef.current?.();
    },
    []
  );

  const [popover, setPopover] = useState<{
    anchor: HTMLElement;
    rowId: string;
    columnId: string;
  } | null>(null);

  const getColumnWidth = (col: Column): number => col.width ?? DEFAULT_COLUMN_WIDTH;
  // The smallest px a column may be dragged to. Rating columns keep the populated chip
  // row legible (it clips under fixed layout, which ignores content); text columns just
  // stay readable. Used as the resize floor so a column can't be shrunk into clipping.
  const colMinPx = (col: Column): number =>
    col.type === 'riskRating' ? MIN_RATING_COLUMN_WIDTH : MIN_TEXT_COLUMN_WIDTH;

  const totalWidth =
    ROW_NUMBER_COLUMN_WIDTH +
    columns.reduce((sum, c) => sum + getColumnWidth(c), 0) +
    (editable ? ACTION_COLUMN_WIDTH : 0);

  const handleResizeMouseDown =
    (columnId: string, startWidth: number) =>
    (e: React.MouseEvent<HTMLDivElement>): void => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const colEl = colRefs.current[columnId];
      const headerEl = headerRefs.current[columnId];
      const tableEl = tableRef.current;
      const initialTableWidth = tableEl?.offsetWidth ?? 0;
      const draggedCol = columns.find((c) => c.id === columnId);
      const dragMinWidth = draggedCol ? colMinPx(draggedCol) : MIN_COLUMN_WIDTH;
      let latestWidth = startWidth;

      dragCleanupRef.current?.();

      let ended = false;
      const teardown = (): void => {
        if (ended) return;
        ended = true;
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
        window.removeEventListener('blur', handleUp);
        dragCleanupRef.current = null;
      };

      const handleMove = (ev: MouseEvent): void => {
        // If the primary button has been released (e.g. mouseup happened over
        // an iframe / dev-error overlay and we never saw it), end the drag.
        if (ev.buttons === 0) {
          handleUp();
          return;
        }
        try {
          latestWidth = Math.max(dragMinWidth, startWidth + (ev.clientX - startX));
          if (colEl) {
            colEl.style.width = `${latestWidth}px`;
            colEl.style.minWidth = `${latestWidth}px`;
          }
          if (headerEl) {
            headerEl.style.width = `${latestWidth}px`;
            headerEl.style.minWidth = `${latestWidth}px`;
          }
          if (tableEl) {
            const newTableWidth = initialTableWidth + (latestWidth - startWidth);
            tableEl.style.width = `${newTableWidth}px`;
            tableEl.style.minWidth = `${newTableWidth}px`;
          }
        } catch {
          // Any DOM error mid-drag must not leave the listeners dangling.
          handleUp();
        }
      };

      const handleUp = (): void => {
        teardown();
        // Don't clear imperative widths here — React's next render will overwrite
        // them with the new state-driven values (same width we just set), so the
        // column never passes through a width-less paint that could collapse it.
        try {
          onColumnResize(columnId, Math.max(dragMinWidth, latestWidth));
        } catch {
          // Swallow — listeners are already removed; surfacing here would
          // re-trigger the error overlay on every mouse move.
        }
      };

      dragCleanupRef.current = teardown;
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
      window.addEventListener('blur', handleUp);
    };

  const matrixRows = severityLabels.length;
  const matrixColumns = likelihoodLabels.length;

  const handleRiskRatingClick = (
    e: React.MouseEvent<HTMLElement>,
    row: Row,
    column: Column
  ): void => {
    if (!editable) return;
    // Stop the click reaching the editor view: otherwise ProseMirror selects the whole
    // atom node and the next transaction scrolls that selection into view, jumping the
    // page to the top of the node.
    e.preventDefault();
    e.stopPropagation();
    setPopover({ anchor: e.currentTarget, rowId: row.id, columnId: column.id });
  };

  const handleMatrixSelect = (likelihood: number, severity: number): void => {
    if (!popover) return;
    onChange(popover.rowId, popover.columnId, { likelihood, severity });
    setPopover(null);
  };

  const handleClearRiskRating = (): void => {
    if (!popover) return;
    onChange(popover.rowId, popover.columnId, { likelihood: null, severity: null });
    setPopover(null);
  };

  const renderCell = (column: Column, row: Row): React.ReactNode => {
    const value = row.values[column.id];
    // Read-only keeps the values legible (normal text colour, no edit affordance) rather
    // than greying them out the way `disabled` would.
    const readOnly = !editable;

    // In a read-only viewer, every text-based cell renders as plain static text rather
    // than a (still-focusable) input, so a published/shared document can't take focus or
    // be edited. Risk-rating cells stay on their own component (rendered non-interactive).
    if (readOnly && column.type !== 'riskRating') {
      const text =
        column.type === 'date'
          ? formatFloating(typeof value === 'string' ? value : null, 'date', SLASH_FULL_DATE_FORMAT)
          : typeof value === 'string'
            ? value
            : '';
      return <StyledReadOnlyCellText>{text}</StyledReadOnlyCellText>;
    }

    switch (column.type) {
      case 'text':
        return (
          <StyledCellInputWrapper>
            <StyledTextField
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={1}
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => onChange(row.id, column.id, e.target.value)}
              InputProps={{ readOnly }}
            />
          </StyledCellInputWrapper>
        );
      case 'riskRating':
        return (
          <RiskRatingCell
            rating={getRiskRating(row.values[column.id])}
            matrixRows={matrixRows}
            matrixColumns={matrixColumns}
            editable={editable}
            onClick={(e) => handleRiskRatingClick(e, row, column)}
          />
        );
      case 'date':
        return (
          <DateCell
            value={value}
            readOnly={readOnly}
            onChange={(next) => onChange(row.id, column.id, next)}
          />
        );
      default:
        return null;
    }
  };

  const colSpan = 1 + columns.length + (editable ? 1 : 0);

  // Only show the legend when the matrix is roomy enough for all bands to be
  // visually distinguishable (small matrices collapse into one or two bands).
  const showLegend = matrixRows >= LEGEND_MIN_AXIS && matrixColumns >= LEGEND_MIN_AXIS;
  const legendBands: { label: string; bg: string }[] = [
    { label: 'Very low', bg: getLegendColor('Very low').bg },
    { label: 'Low', bg: getLegendColor('Low').bg },
    { label: 'Medium', bg: getLegendColor('Medium').bg },
    { label: 'High', bg: getLegendColor('High').bg },
    { label: 'Very high', bg: getLegendColor('Very high').bg },
  ];

  const popoverRating: RiskRatingValue | null = popover
    ? getRiskRating(rows.find((r) => r.id === popover.rowId)?.values[popover.columnId] ?? null)
    : null;

  return (
    <>
      <StyledTableScrollContainer>
        <StyledRiskAssessmentTableWrapper ref={scrollWrapperRef}>
          <StyledRiskAssessmentTable
            ref={tableRef}
            style={{ width: `${totalWidth}px`, minWidth: `${totalWidth}px` }}
          >
          <colgroup>
            <col
              style={{
                width: `${ROW_NUMBER_COLUMN_WIDTH}px`,
                minWidth: `${ROW_NUMBER_COLUMN_WIDTH}px`,
              }}
            />
            {columns.map((col) => {
              const w = getColumnWidth(col);
              return (
                <col
                  key={col.id}
                  ref={(el) => {
                    colRefs.current[col.id] = el;
                  }}
                  style={{ width: `${w}px`, minWidth: `${w}px` }}
                />
              );
            })}
            {editable && (
              <col
                style={{ width: `${ACTION_COLUMN_WIDTH}px`, minWidth: `${ACTION_COLUMN_WIDTH}px` }}
              />
            )}
          </colgroup>
          <thead>
            <tr>
              <StyledRowNumberHeaderCell scope="col" aria-label="Row number">
                #
              </StyledRowNumberHeaderCell>
              {columns.map((col) => {
                const w = getColumnWidth(col);
                return (
                  <StyledHeaderCell
                    key={col.id}
                    ref={(el) => {
                      headerRefs.current[col.id] = el;
                    }}
                    style={{ width: `${w}px`, minWidth: `${w}px` }}
                  >
                    {col.label}
                    {editable && (
                      <StyledColumnResizer
                        role="separator"
                        aria-label={`Resize ${col.label} column`}
                        onMouseDown={handleResizeMouseDown(col.id, w)}
                      />
                    )}
                  </StyledHeaderCell>
                );
              })}
              {editable && (
                <StyledHeaderCell
                  aria-label="Row actions"
                  style={{
                    width: `${ACTION_COLUMN_WIDTH}px`,
                    minWidth: `${ACTION_COLUMN_WIDTH}px`,
                  }}
                />
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <StyledEmptyState colSpan={colSpan}>
                  No rows yet. Click &ldquo;Add row&rdquo; to start.
                </StyledEmptyState>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row.id}>
                  <StyledRowNumberCell>{index + 1}</StyledRowNumberCell>
                  {columns.map((col) => (
                    <StyledBodyCell key={col.id}>{renderCell(col, row)}</StyledBodyCell>
                  ))}
                  {editable && (
                    <StyledRowActionsCell>
                      <StyledRowDeleteButton
                        size="small"
                        onClick={() => onDelete(row.id)}
                        aria-label="Delete row"
                      >
                        <DeleteIcon />
                      </StyledRowDeleteButton>
                    </StyledRowActionsCell>
                  )}
                </tr>
              ))
            )}
          </tbody>
          </StyledRiskAssessmentTable>
        </StyledRiskAssessmentTableWrapper>
        <StyledCustomScrollbar $visible={scrollbar.visible} aria-hidden>
          <StyledCustomScrollbarThumb
            style={{ width: `${scrollbar.widthPct}%`, left: `${scrollbar.leftPct}%` }}
            onMouseDown={handleScrollThumbMouseDown}
          />
        </StyledCustomScrollbar>
      </StyledTableScrollContainer>

      {showLegend && (
        <StyledLegendStrip aria-label="Risk band legend">
          {legendBands.map((band) => (
            <StyledLegendItem key={band.label}>
              <StyledLegendSwatch $bg={band.bg} />
              {band.label}
            </StyledLegendItem>
          ))}
        </StyledLegendStrip>
      )}

      <RiskRatingMatrixPopover
        open={popover !== null}
        anchorEl={popover?.anchor ?? null}
        currentRating={popoverRating}
        likelihoodLabels={likelihoodLabels}
        severityLabels={severityLabels}
        onSelect={handleMatrixSelect}
        onClear={handleClearRiskRating}
        onClose={() => setPopover(null)}
      />
    </>
  );
};
